import { PageDataHandler } from "@/lib/utility/data/page-data-handler";
import { BackendResourceEnum, ResourceEnum } from "@/store/enum/authResourceEnum";
import { PaginatedBaseStore } from "@/store/paginated-base.store";
import store from "@/store/VuexPlugin";
import { Action, getModule, Module } from "vuex-module-decorators";
import { PaginationFilterListElement } from "./base-pagination.store";
import { AiResultModule } from "./ai-result.store";
import { DocumentRuleCheckModule } from "./document-rule-check.store";
import { DocumentRuleModule } from "./document-rule.store";
import { OperationModule } from "./operation.store";
import { ReportPaginationModule } from "./report-pagination.store";

import { EventUIDto, IEventUIDto } from "@/lib/dto/event/event-ui.dto";
import { IReport } from "@/models/report.entity";
import { ISignDocument, SignDocument } from "@/models/sign-document.entity";
import { ISignRequest, SignRequest } from "@/models/sign-request.entity";
import { ISnapshot, Snapshot } from "@/models/snapshot.entity";
import { ITicket, Ticket } from "@/models/ticket.entity";
import { IVehicle, Vehicle } from "@/models/vehicle.entity";
import { ILead, Lead } from "@/models/lead.entity";
import { ILeadGroup, LeadGroup } from "@/models/lead-group.entity";

import { ContractPageDataProvider } from "./page-data-provider/contract.page-data-provider";
import { EventPageDataProvider } from "./page-data-provider/event.page-data-provider";
import { GroupPageDataProvider } from "./page-data-provider/group.page-data-provider";
import { PartnerImagePageDataProvider } from "./page-data-provider/partner-image.page-data-provider";
import { PartnerMessagePageDataProvider } from "./page-data-provider/partner-message.page-data-provider";
import { ReportPageDataProvider } from "./page-data-provider/report.page-data-provider";
import { SignDocumentPageDataProvider } from "./page-data-provider/sign-document.page-data-provider";
import { SignRequestPageDataProvider } from "./page-data-provider/sign-request.page-data-provider";
import { TicketPageDataProvider } from "./page-data-provider/ticket.page-data-provider";
import { VehiclePageDataProvider } from "./page-data-provider/vehicle.page-data-provider";
import { AttachmentRequestPageDataProvider } from "./attachment-request.store";
import { CompanyGroupPageDataProvider } from "./page-data-provider/company-group.page-data-provider.ts";
import { CompanyPageDataProvider } from "./page-data-provider/company.page-data-provider";
import { CostPageDataProvider } from "./page-data-provider/cost.page-data-provider";
import { PersonGroupPageDataProvider } from "./page-data-provider/person-group.page-data-provider.ts";
import { PersonPageDataProvider } from "./page-data-provider/person.page-data-provider";
import { CustomerAccountPageDataProvider } from "./page-data-provider/customer-account.page-data-provider";
import { LeadPageDataProvider } from "./page-data-provider/lead.page-data-provider";
import { LeadGroupPageDataProvider } from "./page-data-provider/lead-group.page-data-provider";

import { ActivityLogDataAccessLayer } from "./access-layers/activity-log-service.access-layer";
import { AttachmentRequestDataAccessLayer } from "./access-layers/attachment-request.access-layer";
import { CompanyGroupDataAccessLayer } from "./access-layers/company-group.access-layer";
import { CompanyDataAccessLayer } from "./access-layers/company.access-layer";
import { CostDataAccessLayer } from "./access-layers/cost.access-layer";
import { EventDataAccessLayer } from "./access-layers/event.access-layer";
import { PartnerImageDataAccessLayer } from "./access-layers/partner-image.access-layer";
import { PartnerMessageDataAccessLayer } from "./access-layers/partner-message.access-layer";
import { PersonGroupDataAccessLayer } from "./access-layers/person-group.access-layer";
import { PersonDataAccessLayer } from "./access-layers/person.access-layer";
import { ReportDataAccessLayer } from "./access-layers/report.access-layer";
import { SignDocumentDataAccessLayer } from "./access-layers/sign-document.access-layer";
import { SignRequestDataAccessLayer } from "./access-layers/sign-request.access-layer";
import { HandoverSnapshotDataAccessLayer } from "./access-layers/snapshot.access-layer";
import { TicketDataAccessLayer } from "./access-layers/ticket.access-layer";
import { VehicleAccessLayer } from "./access-layers/vehicle.access-layer";
import { CustomerAccountDataAccessLayer } from "./access-layers/customer-account.access-layer";
import { LeadDataAccessLayer } from "./access-layers/lead.access-layer";
import { LeadGroupDataAccessLayer } from "./access-layers/lead-group.access-layer";

import { ActivityTypeEnum } from "@/lib/enum/activity-type.enum";
import { PageFilterOperationEnum } from "@/lib/utility/data/page-filter-operation.enum";
import { ActivityLog } from "@/models/activity-log.entity";
import { AttachmentRequest, IAttachmentRequest } from "@/models/attachment-request.entity";
import { IClearanceApproval, ClearanceApproval } from "@/models/clearance-approval.entity";
import { IClearance, Clearance } from "@/models/clearance.entity";
import { CompanyGroup, ICompanyGroup } from "@/models/company-group.entity";
import { Company, ICompany } from "@/models/company.entity";
import { Contract, IContract } from "@/models/contract.entity";
import { Cost, ICost } from "@/models/cost.entity";
import { Fleet, IFleet } from "@/models/fleet.entity";
import { Group, IGroup } from "@/models/group.entity";
import { IInvoice, Invoice } from "@/models/invoice.entity";
import { IPageFilterElement, PageFilterElement } from "@/models/page-filter-element.entity";
import { IPartnerMessage, PartnerMessage } from "@/models/partner-message.entity";
import { IPersonGroup, PersonGroup } from "@/models/person-group.entity";
import { IPerson, Person } from "@/models/person.entity";
import { IRepairEstimate, RepairEstimate } from "@/models/repair-estimate.entity";
import { DocumentRequest, IDocumentRequest } from "@/models/document-request.entity";
import { TimestampDocument } from "@/models/timestamp.entity";
import {
  MrfiktivAppraisalControllerFindAllParamsGen,
  MrfiktivAttachmentRequestControllerGetAllForPartnerParamsGen,
  MrfiktivClearanceApprovalControllerGetParamsGen,
  MrfiktivClearanceControllerGetParamsGen, // Added
  MrfiktivCompanyControllerGetParamsGen,
  MrfiktivCompanyGroupControllerGetParamsGen,
  MrfiktivContractControllerGetParamsGen,
  MrfiktivCostControllerGetParamsGen,
  MrfiktivCreateActivityLogDtoGen,
  MrfiktivCustomerAccountControllerFindAllByPartnerIdParamsGen,
  MrfiktivCustomFieldControllerGetParamsGen,
  MrfiktivEventControllerGetParamsGen,
  MrfiktivFleetControllerFindAllParamsGen,
  MrfiktivGroupControllerGetParamsGen,
  MrfiktivInvoiceControllerGetParamsGen,
  MrfiktivLeadControllerFindAllParamsGen,
  MrfiktivLeadGroupControllerGetParamsGen,
  MrfiktivPartnerCustomerUserViewModelGen,
  MrfiktivPartnerImageViewModelGen,
  MrfiktivPartnerMessageControllerFindAllByPartnerIdParamsGen,
  MrfiktivPartnerVideoControllerGetParamsGen,
  MrfiktivPersonControllerGetParamsGen,
  MrfiktivPersonGroupControllerGetParamsGen,
  MrfiktivProjectControllerGetParamsGen,
  MrfiktivRealtimeSessionControllerFindAllParamsGen,
  MrfiktivRentalControllerGetParamsGen,
  MrfiktivRepairEstimateControllerGetParamsGen,
  MrfiktivDocumentRequestControllerFindAllParamsGen,
  MrfiktivReportControllerFindAllParamsGen,
  MrfiktivReportGroupControllerGetParamsGen,
  MrfiktivTagControllerFindAllByPartnerIdParamsGen,
  MrfiktivTicketControllerGetParamsGen,
  MrfiktivVehicleControllerGetAllParamsGen
} from "@/services/mrfiktiv/v1/data-contracts";
import {
  SignDocumentControllerFindAllParamsGen,
  SignSignRequestControllerFindAllParamsGen
} from "@/services/sign/v1/data-contracts";
import { BaseStore } from "../base.store";
import { ActionEnum } from "../enum/authActionEnum";
import { ClearanceApprovalDataAccessLayer } from "./access-layers/clearance-approval.access-layer";
import { ClearanceDataAccessLayer } from "./access-layers/clearance.access-layer";
import { ContractDataAccessLayer } from "./access-layers/contract.access-layer";
import { FleetDataAccessLayer } from "./access-layers/fleet.access-layer";
import { GroupDataAccessLayer } from "./access-layers/group.access-layer";
import { InvoiceDataAccessLayer } from "./access-layers/invoice.access-layer";
import { RepairEstimateDataAccessLayer } from "./access-layers/repair-estimate.access-layer";
import { DocumentRequestDataAccessLayer } from "./access-layers/document-request.access-layer";
import { IPartnerMessageStore } from "./inbox-message.store";
import { UserModule } from "./me-user.store";
import { ClearanceApprovalPageDataProvider } from "./page-data-provider/clearance-approval.page-data-provider";
import { ClearancePageDataProvider } from "./page-data-provider/clearance.page-data-provider";
import { FleetPageDataProvider } from "./page-data-provider/fleet.page-data-provider";
import { InvoicePageDataProvider } from "./page-data-provider/invoice.page-data-provider";
import { RepairEstimatePageDataProvider } from "./page-data-provider/repair-estimate.page-data-provider";
import { DocumentRequestPageDataProvider } from "./page-data-provider/document-request.page-data-provider";
import { ITag, Tag } from "@/models/tag.entity";
import { TagDataAccessLayer } from "./access-layers/tag.access-layer";
import { TagPageDataProvider } from "./page-data-provider/tag.page-data-provider";
import { IRental, Rental } from "@/models/rental.entity";
import { RentalDataAccessLayer } from "./access-layers/rental.access-layer";
import { RentalPageDataProvider } from "./page-data-provider/rental.page-data-provider";
import { AppraisalDataAccessLayer } from "./access-layers/appraisal.access-layer";
import { AppraisalPageDataProvider } from "./page-data-provider/appraisal.page-data-provider";
import { Appraisal, IAppraisal } from "@/models/appraisal.entity";
import { ICustomField, CustomField } from "@/models/custom-field.entity";
import { CustomFieldDataAccessLayer } from "./access-layers/custom-field.access-layer";
import { CustomFieldPageDataProvider } from "./page-data-provider/custom-field.page-data-provider";
import { ReportGroupPageDataProvider } from "./page-data-provider/report-group.page-data-provider.ts";
import { ReportGroupDataAccessLayer } from "./access-layers/report-group.access-layer";
import { IReportGroup } from "@/models/report-group.entity";
import { ConfigModule } from "./config";
import { AppContextEnum } from "@/lib/enum/appContext.enum";
import { IVideo, Video } from "@/models/video.entity";
import { VideoDataAccessLayer } from "./access-layers/video.access-layer";
import { VideoPageDataProvider } from "./page-data-provider/video.page-data-provider";
import { ProgressStatusEnum } from "../enum/partner/progress.status.enum";
import { CustomerAccountModule } from "./customer-account.store";
import { CallPageDataProvider } from "./page-data-provider/call.page-data-provider";
import { CallDataAccessLayer } from "./access-layers/call.access-layer";
import { Call, ICall } from "@/models/call.entity";
import { ProjectPageDataProvider } from "./project.store";
import { ProjectDataAccessLayer } from "./access-layers/project.access-layer";
import { IProject, Project } from "@/models/project.entity";
@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-call",
  store
})
class RefsCallStore extends PaginatedBaseStore<ICall, MrfiktivRealtimeSessionControllerFindAllParamsGen> {
  _data = CallDataAccessLayer;
  _pageProvider = CallPageDataProvider;
  _pager = new PageDataHandler(this._data, this._pageProvider);
  filterOptions: PaginationFilterListElement[] = Call.filterables;
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-lead",
  store
})
class RefsLeadStore extends PaginatedBaseStore<ILead, MrfiktivLeadControllerFindAllParamsGen> {
  _data = LeadDataAccessLayer;
  _pageProvider = LeadPageDataProvider;
  _pager = new PageDataHandler(this._data, this._pageProvider);
  filterOptions: PaginationFilterListElement[] = Lead.filterables;
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-lead-group",
  store
})
class RefsLeadGroupStore extends PaginatedBaseStore<ILeadGroup, MrfiktivLeadGroupControllerGetParamsGen> {
  _data = LeadGroupDataAccessLayer;
  _pageProvider = LeadGroupPageDataProvider;
  _pager = new PageDataHandler(this._data, this._pageProvider);
  filterOptions: PaginationFilterListElement[] = LeadGroup.filterables;
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-customer-account",
  store
})
class RefsCustomerAccountStore extends PaginatedBaseStore<
  MrfiktivPartnerCustomerUserViewModelGen,
  MrfiktivCustomerAccountControllerFindAllByPartnerIdParamsGen
> {
  _data = CustomerAccountDataAccessLayer;
  _pageProvider = CustomerAccountPageDataProvider;
  _pager = new PageDataHandler(this._data, this._pageProvider);
  filterOptions: PaginationFilterListElement[] = CustomerAccountModule.filterOptions;
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-vehicle",
  store
})
class RefsVehicleStore extends PaginatedBaseStore<IVehicle, MrfiktivVehicleControllerGetAllParamsGen> {
  _data = VehicleAccessLayer;
  _pageProvider = VehiclePageDataProvider;
  _pager = new PageDataHandler(this._data, this._pageProvider);
  filterOptions: PaginationFilterListElement[] = Vehicle.filterables;

  filter: IPageFilterElement[] = [
    new PageFilterElement({ key: "archived.at", value: "false", operation: PageFilterOperationEnum.EXISTS })
  ];

  /**
   * Vehicle reference pickers must never surface soft-deleted vehicles,
   * so this is a hiddenFilter rather than a regular, removable filter.
   */
  hiddenFilter: IPageFilterElement[] = [
    new PageFilterElement({ key: "deleted.at", value: "false", operation: PageFilterOperationEnum.EXISTS })
  ];
}

/**
 * wraps the timed event endpoint into a makeshift event pagination @see RefTypeMap
 */
@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-event",
  store
})
class RefsEventStore extends PaginatedBaseStore<IEventUIDto, MrfiktivEventControllerGetParamsGen> {
  protected _data = EventDataAccessLayer;
  protected _pageProvider = EventPageDataProvider;
  protected _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = EventUIDto.filterables;

  @Action
  addToList(document: IEventUIDto): void {
    this._data.set(document);
  }
}

/**
 * wraps the timed event endpoint into a makeshift event pagination @see RefTypeMap
 */
@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-report",
  store
})
class RefsReportStore extends PaginatedBaseStore<IReport, MrfiktivReportControllerFindAllParamsGen> {
  protected _data = ReportDataAccessLayer;
  protected _pageProvider = ReportPageDataProvider;
  protected _pager = new PageDataHandler(this._data, this._pageProvider);

  hiddenFilter: IPageFilterElement[] = [
    new PageFilterElement({ key: "progressStatus", value: ProgressStatusEnum.DELETED, operation: "$ne" })
  ];

  filterOptions: PaginationFilterListElement[] = ReportPaginationModule.filterOptions;

  @Action
  addToList(document: IReport): void {
    this._data.set(document);
  }
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-report-group-store",
  store
})
export class RefsReportGroupStore extends PaginatedBaseStore<IReportGroup, MrfiktivReportGroupControllerGetParamsGen> {
  protected _data = ReportGroupDataAccessLayer;
  protected _pageProvider = ReportGroupPageDataProvider;
  protected _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = PersonGroup.filterables;
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-project",
  store
})
export class RefsProjectStore extends PaginatedBaseStore<IProject, MrfiktivProjectControllerGetParamsGen> {
  _data = ProjectDataAccessLayer;
  _pageProvider = new ProjectPageDataProvider();
  _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = Project.filterables;
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-ticket",
  store
})
export class RefsTicketStore extends PaginatedBaseStore<ITicket, MrfiktivTicketControllerGetParamsGen> {
  _data = TicketDataAccessLayer;
  _pageProvider = TicketPageDataProvider;
  _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = Ticket.filterables;
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-sign-document",
  store
})
export class RefsSignDocumentStore extends PaginatedBaseStore<ISignDocument, SignDocumentControllerFindAllParamsGen> {
  _data = SignDocumentDataAccessLayer;
  _pageProvider = SignDocumentPageDataProvider;
  _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = SignDocument.filterables;
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-snapshot",
  store
})
export class RefsSnapshotStore extends BaseStore<ISnapshot> {
  _data = HandoverSnapshotDataAccessLayer;
  _pageProvider = SignDocumentPageDataProvider;

  filterOptions: PaginationFilterListElement[] = Snapshot.filterables;
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-sign-request",
  store
})
export class RefsSignRequestStore extends PaginatedBaseStore<ISignRequest, SignSignRequestControllerFindAllParamsGen> {
  _data = SignRequestDataAccessLayer;
  _pageProvider = SignRequestPageDataProvider;
  _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = SignRequest.filterables;
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-attachment-request",
  store
})
export class RefsAttachmentRequestStore extends PaginatedBaseStore<
  IAttachmentRequest,
  MrfiktivAttachmentRequestControllerGetAllForPartnerParamsGen
> {
  _data = AttachmentRequestDataAccessLayer;
  _pageProvider = new AttachmentRequestPageDataProvider();
  _pager = new PageDataHandler<IAttachmentRequest, MrfiktivAttachmentRequestControllerGetAllForPartnerParamsGen>(
    this._data,
    this._pageProvider
  );

  filterOptions: PaginationFilterListElement[] = AttachmentRequest.filterables;
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-message-store",
  store
})
export class RefsMessagesStore
  extends PaginatedBaseStore<IPartnerMessage, MrfiktivPartnerMessageControllerFindAllByPartnerIdParamsGen>
  implements IPartnerMessageStore
{
  _data = PartnerMessageDataAccessLayer;
  _pageProvider = PartnerMessagePageDataProvider;
  _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = PartnerMessage.filterables;

  @Action
  async generateFrontendTimeLineElementsForMessagesAssociatedWithReference({
    refId,
    partnerId
  }: {
    refId: string;
    partnerId: string;
  }): Promise<void> {
    await this._generateTimeLineMessagesForFilter({
      partnerId,
      refId: refId,
      refType: BackendResourceEnum.REPORT,
      filters: [new PageFilterElement({ value: refId, key: "refs.refId", operation: PageFilterOperationEnum.EQUAL })]
    });
  }

  @Action
  async generateTimeLineMessagesForUser(data: {
    id: string;
    userName: string;
    contact?: { email: string };
    partnerId: string;
    resource: BackendResourceEnum.USER | BackendResourceEnum.CUSTOMER_DATA;
  }) {
    if (!data?.userName) {
      throw new Error("no user");
    }
    await this.generateTimeLineMessagesForMail({
      refId: data.id,
      refType: data.resource,
      mail: data.userName,
      partnerId: data.partnerId
    });

    if (data.contact?.email && data.contact?.email !== data.userName) {
      await this.generateTimeLineMessagesForMail({
        refId: data.id,
        refType: data.resource,
        mail: data.contact.email,
        partnerId: data.partnerId
      });
    }
  }

  @Action
  async generateTimeLineMessagesForMail(data: {
    refId: string;
    refType: BackendResourceEnum;
    partnerId: string;
    mail: string;
  }): Promise<void> {
    await this._generateTimeLineMessagesForFilter({
      ...data,
      filters: [new PageFilterElement({ value: data.mail, key: "from", operation: PageFilterOperationEnum.EQUAL })]
    });

    await this._generateTimeLineMessagesForFilter({
      ...data,
      filters: [new PageFilterElement({ value: data.mail, key: "to", operation: PageFilterOperationEnum.EQUAL })]
    });
  }

  @Action
  private async _generateTimeLineMessagesForFilter({
    refId,
    refType,
    partnerId,
    filters
  }: {
    refId: string;
    refType: BackendResourceEnum;
    partnerId: string;
    filters: IPageFilterElement[];
  }): Promise<void> {
    if (!UserModule.abilities.can(ActionEnum.READ, BackendResourceEnum.MESSAGE, partnerId)) {
      return;
    }

    if (
      ConfigModule.appContext !== AppContextEnum.REPORT_PORTAL &&
      ConfigModule.appContext !== AppContextEnum.THG_PORTAL
    ) {
      return;
    }

    this.setFilters(filters);
    for (const message of this.filtered) {
      this.createLocalActivityLogForMessage({
        refType,
        refId,
        partnerId,
        message
      });
    }

    await this.fetchFirstPage({ partnerId });
    for (const message of this.filtered) {
      this.createLocalActivityLogForMessage({
        refType,
        refId,
        partnerId,
        message
      });
    }
  }

  @Action
  private createLocalActivityLogForMessage({
    refType,
    refId,
    partnerId,
    message
  }: {
    refType: BackendResourceEnum;
    refId: string;
    partnerId: string;
    message: IPartnerMessage;
  }) {
    const data: MrfiktivCreateActivityLogDtoGen = {
      source: { refType: refType, refId: refId },
      target: [{ refType: BackendResourceEnum.MESSAGE, refId: message.id }],
      actionType: ActionEnum.CREATE,
      activity: ActivityTypeEnum.MESSAGE
    };
    const activityLog = new ActivityLog({
      ...data,
      id: refType + message.id,
      partnerId: partnerId,
      timestamp: new TimestampDocument(message.timestamp),
      userId: message.userId,
      isDeletable: false
    });

    ActivityLogDataAccessLayer.set(activityLog);
  }
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-image-store",
  store
})
export class RefsImageStore extends PaginatedBaseStore<
  MrfiktivPartnerImageViewModelGen,
  MrfiktivReportControllerFindAllParamsGen
> {
  _data = PartnerImageDataAccessLayer;
  _pageProvider = PartnerImagePageDataProvider;
  _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = [];
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-cost-store",
  store
})
export class RefsCostStore extends PaginatedBaseStore<ICost, MrfiktivCostControllerGetParamsGen> {
  protected _data = CostDataAccessLayer;
  protected _pageProvider = CostPageDataProvider;
  protected _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = Cost.filterables;
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-company-store",
  store
})
export class RefsCompanyStore extends PaginatedBaseStore<ICompany, MrfiktivCompanyControllerGetParamsGen> {
  protected _data = CompanyDataAccessLayer;
  protected _pageProvider = CompanyPageDataProvider;
  protected _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = Company.filterables;
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-company-group-store",
  store
})
export class RefsCompanyGroupStore extends PaginatedBaseStore<
  ICompanyGroup,
  MrfiktivCompanyGroupControllerGetParamsGen
> {
  protected _data = CompanyGroupDataAccessLayer;
  protected _pageProvider = CompanyGroupPageDataProvider;
  protected _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = CompanyGroup.filterables;
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-person-store",
  store
})
export class RefsPersonStore extends PaginatedBaseStore<IPerson, MrfiktivPersonControllerGetParamsGen> {
  protected _data = PersonDataAccessLayer;
  protected _pageProvider = PersonPageDataProvider;
  protected _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = Person.filterables;
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-person-group-store",
  store
})
export class RefsPersonGroupStore extends PaginatedBaseStore<IPersonGroup, MrfiktivPersonGroupControllerGetParamsGen> {
  protected _data = PersonGroupDataAccessLayer;
  protected _pageProvider = PersonGroupPageDataProvider;
  protected _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = PersonGroup.filterables;
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-group-store",
  store
})
export class RefsGroupStore extends PaginatedBaseStore<IGroup, MrfiktivGroupControllerGetParamsGen> {
  protected _data = GroupDataAccessLayer;
  protected _pageProvider = GroupPageDataProvider;
  protected _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = Group.filterables;
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-contract-store",
  store
})
export class RefsContractStore extends PaginatedBaseStore<IContract, MrfiktivContractControllerGetParamsGen> {
  protected _data = ContractDataAccessLayer;
  protected _pageProvider = ContractPageDataProvider;
  protected _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = Contract.filterables;
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-contract-group-store",
  store
})
export class RefsContractGroupStore extends PaginatedBaseStore<IGroup, MrfiktivGroupControllerGetParamsGen> {
  protected _data = GroupDataAccessLayer;
  protected _pageProvider = GroupPageDataProvider;
  protected _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = Group.filterables;

  hiddenFilter: IPageFilterElement[] = [
    new PageFilterElement({
      key: "types",
      operation: PageFilterOperationEnum.EQUAL,
      value: BackendResourceEnum.CONTRACT
    })
  ];
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-fleet",
  store
})
export class RefsFleetStore extends PaginatedBaseStore<IFleet, MrfiktivFleetControllerFindAllParamsGen> {
  protected _data = FleetDataAccessLayer;
  protected _pageProvider = FleetPageDataProvider;
  protected _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = Fleet.filterables;
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-invoice-store",
  store
})
export class RefsInvoiceStore extends PaginatedBaseStore<IInvoice, MrfiktivInvoiceControllerGetParamsGen> {
  protected _data = InvoiceDataAccessLayer;
  protected _pageProvider = InvoicePageDataProvider;
  protected _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = Invoice.filterables;
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-repair-estimate-store",
  store
})
export class RefsRepairEstimateStore extends PaginatedBaseStore<
  IRepairEstimate,
  MrfiktivRepairEstimateControllerGetParamsGen
> {
  protected _data = RepairEstimateDataAccessLayer;
  protected _pageProvider = RepairEstimatePageDataProvider;
  protected _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = RepairEstimate.filterables;
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-document-request-store",
  store
})
export class RefsDocumentRequestStore extends PaginatedBaseStore<
  IDocumentRequest,
  MrfiktivDocumentRequestControllerFindAllParamsGen
> {
  protected _data = DocumentRequestDataAccessLayer;
  protected _pageProvider = DocumentRequestPageDataProvider;
  protected _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = DocumentRequest.filterables;
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-clearance-approval-store",
  store
})
export class RefsClearanceApprovalStore extends PaginatedBaseStore<
  IClearanceApproval,
  MrfiktivClearanceApprovalControllerGetParamsGen
> {
  protected _data = ClearanceApprovalDataAccessLayer;
  protected _pageProvider = ClearanceApprovalPageDataProvider;
  protected _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = ClearanceApproval.filterables;
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-clearance-store",
  store
})
export class RefsClearanceStore extends PaginatedBaseStore<IClearance, MrfiktivClearanceControllerGetParamsGen> {
  protected _data = ClearanceDataAccessLayer;
  protected _pageProvider = ClearancePageDataProvider;
  protected _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = Clearance.filterables;
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-tag-store",
  store
})
export class RefsTagStore extends PaginatedBaseStore<ITag, MrfiktivTagControllerFindAllByPartnerIdParamsGen> {
  protected _data = TagDataAccessLayer;
  protected _pageProvider = TagPageDataProvider;
  protected _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = Tag.filterables;
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-rental-store",
  store
})
export class RefsRentalStore extends PaginatedBaseStore<IRental, MrfiktivRentalControllerGetParamsGen> {
  protected _data = RentalDataAccessLayer;
  protected _pageProvider = RentalPageDataProvider;
  protected _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = Rental.filterables;
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-appraisal-store",
  store
})
export class RefsAppraisalStore extends PaginatedBaseStore<IAppraisal, MrfiktivAppraisalControllerFindAllParamsGen> {
  protected _data = AppraisalDataAccessLayer;
  protected _pageProvider = AppraisalPageDataProvider;
  protected _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = Appraisal.filterables;
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-custom-field-store",
  store
})
export class RefsCustomFieldStore extends PaginatedBaseStore<ICustomField, MrfiktivCustomFieldControllerGetParamsGen> {
  protected _data = CustomFieldDataAccessLayer;
  protected _pageProvider = CustomFieldPageDataProvider;
  protected _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = CustomField.filterables;
}

/**
 * wraps the timed event endpoint into a makeshift event pagination @see RefTypeMap
 */
@Module({
  dynamic: true,
  namespaced: true,
  name: "refs-video",
  store
})
class RefsVideoStore extends PaginatedBaseStore<IVideo, MrfiktivPartnerVideoControllerGetParamsGen> {
  protected _data = VideoDataAccessLayer;
  protected _pageProvider = VideoPageDataProvider;
  protected _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = Video.filterables;
}

export const RefsCustomerAccountModule = getModule(RefsCustomerAccountStore);
export const RefsCallModule = getModule(RefsCallStore);
export const RefsLeadModule = getModule(RefsLeadStore);
export const RefsLeadGroupModule = getModule(RefsLeadGroupStore);
export const RefsVehicleModule = getModule(RefsVehicleStore);
export const RefsEventModule = getModule(RefsEventStore);
export const RefsReportModule = getModule(RefsReportStore);
export const RefsReportGroupModule = getModule(RefsReportGroupStore);
export const RefsProjectModule = getModule(RefsProjectStore);
export const RefsTicketModule = getModule(RefsTicketStore);
export const RefsSignDocumentModule = getModule(RefsSignDocumentStore);
export const RefsSnapshotModule = getModule(RefsSnapshotStore);
export const RefsSignRequestModule = getModule(RefsSignRequestStore);
export const RefAttachmentRequestModule = getModule(RefsAttachmentRequestStore);
export const RefsImageModule = getModule(RefsImageStore);
export const RefsMessagesModule = getModule(RefsMessagesStore);
export const RefsCostModule = getModule(RefsCostStore);
export const RefsCompanyModule = getModule(RefsCompanyStore);
export const RefsCompanyGroupModule = getModule(RefsCompanyGroupStore);
export const RefsPersonModule = getModule(RefsPersonStore);
export const RefsPersonGroupModule = getModule(RefsPersonGroupStore);
export const RefsGroupModule = getModule(RefsGroupStore);
export const RefsContractModule = getModule(RefsContractStore);
export const RefsContractGroupModule = getModule(RefsContractGroupStore);
export const RefsFleetModule = getModule(RefsFleetStore);
export const RefsInvoiceModule = getModule(RefsInvoiceStore);
export const RefsRepairEstimateModule = getModule(RefsRepairEstimateStore);
export const RefsDocumentRequestModule = getModule(RefsDocumentRequestStore);
export const RefsClearanceApprovalModule = getModule(RefsClearanceApprovalStore);
export const RefsClearanceModule = getModule(RefsClearanceStore);
export const RefsTagModule = getModule(RefsTagStore);
export const RefsRentalModule = getModule(RefsRentalStore);
export const RefsAppraisalModule = getModule(RefsAppraisalStore);
export const RefsCustomFieldModule = getModule(RefsCustomFieldStore);
export const RefsVideoModule = getModule(RefsVideoStore);

/**
 * Maps reference types to their respective modules and icons
 */
export const RefTypeMap: Map<ResourceEnum, { module: PaginatedBaseStore<any, any>; icon: string; component?: string }> =
  (() => {
    const map = new Map();
    map.set(ResourceEnum.CALL, {
      module: RefsCallModule,
      icon: "mdi-phone",
      component: "refs-call"
    });
    map.set(ResourceEnum.LEAD, {
      module: RefsLeadModule,
      icon: "mdi-cart-plus",
      component: "refs-lead"
    });
    map.set(ResourceEnum.LEAD_GROUP, {
      module: RefsLeadGroupModule,
      icon: "mdi-cart-variant",
      component: "refs-lead-group"
    });
    map.set(ResourceEnum.CUSTOMER_DATA, {
      module: RefsCustomerAccountModule,
      icon: "mdi-account",
      component: "refs-customer-account"
    });
    map.set(ResourceEnum.VEHICLE, {
      module: RefsVehicleModule,
      icon: "mdi-car",
      component: "refs-vehicle"
    });
    map.set(ResourceEnum.REPORT, {
      module: RefsReportModule,
      icon: "mdi-car-wrench",
      component: "refs-report"
    });
    map.set(ResourceEnum.REPORT_GROUP, {
      module: RefsReportGroupModule,
      icon: "mdi-bookshelf",
      component: "refs-report-group"
    });
    map.set(ResourceEnum.EVENT, {
      module: RefsEventModule,
      icon: "mdi-calendar",
      component: "refs-event"
    });
    map.set(ResourceEnum.PROJECT, {
      module: RefsProjectModule,
      icon: "mdi-check-all",
      component: "refs-project"
    });
    map.set(ResourceEnum.TICKET, {
      module: RefsTicketModule,
      icon: "mdi-check-circle-outline",
      component: "refs-ticket"
    });
    map.set(ResourceEnum.DOCUMENT, {
      module: RefsSignDocumentModule,
      icon: "mdi-file-multiple-outline",
      component: "refs-document"
    });
    map.set(ResourceEnum.SNAPSHOT, {
      module: RefsSnapshotModule,
      icon: "mdi-clipboard-text-outline",
      component: "refs-snapshot"
    });
    map.set(ResourceEnum.SIGN, {
      module: RefsSignRequestModule,
      icon: "mdi-signature",
      component: "refs-sign-request"
    });
    map.set(ResourceEnum.ATTACHMENT_REQUEST, {
      module: RefAttachmentRequestModule,
      icon: "mdi-paperclip",
      component: "refs-attachment-request"
    });
    map.set(ResourceEnum.IMAGE, {
      module: RefsImageModule,
      icon: "mdi-image"
    });
    map.set(ResourceEnum.MESSAGE, {
      module: RefsMessagesModule,
      icon: "mdi-email-outline",
      component: "refs-message"
    });
    map.set(ResourceEnum.COST, {
      module: RefsCostModule,
      icon: "mdi-cash-multiple",
      component: "refs-cost"
    });
    map.set(ResourceEnum.COMPANY, {
      module: RefsCompanyModule,
      icon: "mdi-office-building",
      component: "refs-company"
    });
    map.set(ResourceEnum.COMPANY_GROUP, {
      module: RefsCompanyGroupModule,
      icon: "mdi-domain",
      component: "refs-company-group"
    });
    map.set(ResourceEnum.PERSON, {
      module: RefsPersonModule,
      icon: "mdi-account",
      component: "refs-person"
    });
    map.set(ResourceEnum.PERSON_GROUP, {
      module: RefsPersonGroupModule,
      icon: "mdi-account",
      component: "refs-person-group"
    });
    map.set(ResourceEnum.GROUP, {
      module: RefsGroupModule,
      icon: "mdi-animation-outline",
      component: "refs-group"
    });
    map.set(ResourceEnum.CONTRACT, {
      module: RefsContractModule,
      icon: "mdi-file-document-outline",
      component: "refs-contract"
    });
    map.set(ResourceEnum.FLEET, {
      module: RefsFleetModule,
      icon: "mdi-garage-variant",
      component: "refs-fleet"
    });
    map.set(ResourceEnum.INVOICE, {
      module: RefsInvoiceModule,
      icon: "mdi-invoice-list-outline",
      component: "refs-invoice"
    });
    map.set(ResourceEnum.REPAIR_ESTIMATE, {
      module: RefsRepairEstimateModule,
      icon: "mdi-file-certificate-outline",
      component: "refs-repair-estimate"
    });
    map.set(ResourceEnum.DOCUMENT_REQUEST, {
      module: RefsDocumentRequestModule,
      icon: "mdi-file-document-edit-outline",
      component: "refs-document-request"
    });
    map.set(ResourceEnum.CLEARANCE_APPROVAL, {
      module: RefsClearanceApprovalModule,
      icon: "mdi-file-question-outline",
      component: "refs-clearance-approval"
    });
    map.set(ResourceEnum.CLEARANCE, {
      module: RefsClearanceModule,
      icon: "mdi-file-check-outline",
      component: "refs-clearance"
    });
    map.set(ResourceEnum.TAG, {
      module: RefsTagModule,
      icon: "mdi-tag-outline"
    });

    map.set(ResourceEnum.CUSTOM_FIELD, {
      module: RefsCustomFieldModule,
      icon: "mdi-form-textbox",
      component: "refs-custom-field"
    });

    map.set(ResourceEnum.RENTAL, {
      module: RefsRentalModule,
      icon: "mdi-car-key",
      component: "refs-rental"
    });

    map.set(ResourceEnum.APPRAISAL, {
      module: RefsAppraisalModule,
      icon: "mdi-file-sign",
      component: "refs-appraisal"
    });

    map.set(ResourceEnum.VIDEO, {
      module: RefsVideoModule,
      icon: "mdi-video-outline",
      component: "refs-video"
    });

    map.set(ResourceEnum.DOCUMENT_RULE_CHECK, {
      module: DocumentRuleCheckModule,
      icon: "mdi-file-search-outline",
      component: "refs-document-rule-check"
    });

    map.set(ResourceEnum.DOCUMENT_RULE, {
      module: DocumentRuleModule,
      icon: "mdi-format-list-checks",
      component: "refs-document-rule"
    });

    map.set(ResourceEnum.OPERATION, {
      module: OperationModule,
      icon: "mdi-cog-sync-outline",
      component: "refs-operation"
    });

    map.set(ResourceEnum.AI, {
      module: AiResultModule,
      icon: "mdi-creation",
      component: "refs-ai"
    });

    map.set("", { module: undefined, icon: "mdi-progress-question" });

    return map;
  })();
